
import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

/**
 * PricingBackground component renders the 3D spheres background effect
 */
const PricingBackground: React.FC = () => {
  const threeContainerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!threeContainerRef.current) return;
    
    // Set up scene
    const container = threeContainerRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, container.clientWidth / container.clientHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    
    renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(renderer.domElement);
    
    // Create spheres in different sizes
    const createSphere = (radius: number, color: string, x: number, y: number, z: number) => {
      const geometry = new THREE.SphereGeometry(radius, 32, 32);
      const material = new THREE.MeshPhongMaterial({
        color: new THREE.Color(color),
        transparent: true,
        opacity: 0.7,
        shininess: 100,
      });
      const sphere = new THREE.Mesh(geometry, material);
      sphere.position.set(x, y, z);
      return sphere;
    };
    
    // Add spheres
    const sphere1 = createSphere(1.5, '#0EA5E9', -3, 0, -5);
    const sphere2 = createSphere(1, '#38BDF8', 2, -1, -3);
    const sphere3 = createSphere(0.7, '#7DD3FC', 0, 1, -4);
    
    scene.add(sphere1, sphere2, sphere3);
    
    // Add lighting
    const ambientLight = new THREE.AmbientLight(0x404040, 1.5);
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);
    
    // Position camera
    camera.position.z = 5;
    
    // Animation loop
    let frame: number;
    const animate = () => {
      frame = requestAnimationFrame(animate);
      
      sphere1.rotation.x += 0.003;
      sphere1.rotation.y += 0.004;
      
      sphere2.rotation.x += 0.004;
      sphere2.rotation.y += 0.002;
      
      sphere3.rotation.x += 0.002;
      sphere3.rotation.y += 0.003;
      
      renderer.render(scene, camera);
    };
    
    animate();
    
    // Handle window resize
    const handleResize = () => {
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleResize);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  return <div ref={threeContainerRef} className="absolute inset-0 -z-10 opacity-50" />;
};

export default PricingBackground;
